import React from "react";
import ReactMarkdown from "react-markdown";
import { Bot, Check, Lock, Star, Headphones } from "lucide-react";
import { Avatar, cn } from "../ui";
import { dayLabel, splitQuickReplies, toMs, visitorName } from "../../utils/liveChat";

const isVisitor = (m) => m.sender === "user" || m.sender === "human";

const Divider = ({ children, tone = "default" }) => (
  <div className="my-5 flex items-center gap-3">
    <div className={cn("h-px flex-1", tone === "warning" ? "bg-amber-200" : "bg-zinc-200")} />
    <span className={cn(
      "rounded-md border px-2 py-0.5 text-[11px] font-medium",
      tone === "warning" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-zinc-200 bg-white text-zinc-500"
    )}>
      {children}
    </span>
    <div className={cn("h-px flex-1", tone === "warning" ? "bg-amber-200" : "bg-zinc-200")} />
  </div>
);

const SenderIcon = ({ message, session }) => {
  if (isVisitor(message)) return <Avatar name={visitorName(session)} seed={session && session.session_id} size="sm" />;
  if (message.sender === "human_agent") {
    return <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-white"><Headphones className="h-3.5 w-3.5" /></div>;
  }
  if (message.sender === "private_note") {
    return <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700"><Lock className="h-3.5 w-3.5" /></div>;
  }
  return <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-600"><Bot className="h-3.5 w-3.5" /></div>;
};

const senderLabel = (message, session) => {
  if (isVisitor(message)) return visitorName(session);
  if (message.sender === "human_agent") return message.agentName || "Agent";
  if (message.sender === "private_note") return "Private note";
  return "Bot";
};

const Bubble = ({ message, session, picked }) => {
  const visitor = isVisitor(message);
  const agent = message.sender === "human_agent";
  const note = message.sender === "private_note";
  const { body, options } = !visitor && !note ? splitQuickReplies(message.text) : { body: message.text, options: [] };

  return (
    <div className={cn("flex items-end gap-2", visitor ? "justify-start" : "flex-row-reverse")}>
      <SenderIcon message={message} session={session} />
      <div className={cn("flex max-w-[72%] flex-col", visitor ? "items-start" : "items-end")}>
        <div className={cn("mb-1 flex items-center gap-1.5 text-[11px]", !visitor && "justify-end")}>
          <span className={cn("font-medium", note ? "text-amber-700" : "text-zinc-700")}>{senderLabel(message, session)}</span>
          {message.timestamp && <span className="text-zinc-400">{message.timestamp}</span>}
          {note && <span className="text-amber-600">· only agents see this</span>}
        </div>
        <div className={cn(
          "rounded-xl border px-3.5 py-2.5 text-[13px] leading-relaxed",
          visitor && "rounded-bl-sm border-zinc-200 bg-white text-zinc-900",
          agent && "rounded-br-sm border-zinc-900 bg-zinc-900 text-white",
          note && "rounded-br-sm border-amber-200 bg-amber-50 text-amber-950",
          !visitor && !agent && !note && "rounded-br-sm border-zinc-200 bg-zinc-50 text-zinc-900"
        )}>
          {body && (
            <div className={cn("markdown-content break-words [overflow-wrap:anywhere]", agent && "[&_a]:text-white")}>
              <ReactMarkdown>{body}</ReactMarkdown>
            </div>
          )}
          {options.length > 0 && (
            <div className={cn("flex flex-wrap gap-1.5", body && "mt-2")}>
              {options.map((o) => (
                <span key={o} className={cn(
                  "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs",
                  picked === o ? "border-zinc-900 bg-white font-medium text-zinc-900" : "border-zinc-200 bg-white text-zinc-600"
                )}>
                  {picked === o && <Check className="h-3 w-3" />}
                  {o}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MessageList = ({ messages, session, isResolved }) => {
  const requestedMs = session ? toMs(session.handoff_requested_at) : 0;
  let lastDay = "";
  let shownHandoff = false;
  let shownAgentJoined = false;
  const out = [];

  messages.forEach((m, idx) => {
    const day = dayLabel(m.ts);
    if (day && day !== lastDay) {
      out.push(<Divider key={`day_${idx}`}>{day}</Divider>);
      lastDay = day;
    }
    if (!shownHandoff && requestedMs && m.ts && m.ts >= requestedMs) {
      shownHandoff = true;
      out.push(<Divider key={`handoff_${idx}`} tone="warning">Visitor asked for an agent</Divider>);
    }
    if (!shownAgentJoined && m.sender === "human_agent") {
      shownAgentJoined = true;
      out.push(<Divider key={`agent_${idx}`}>Agent joined the chat</Divider>);
    }
    let picked = null;
    if (!isVisitor(m)) {
      const next = messages.slice(idx + 1).find((x) => x.sender !== "private_note");
      if (next && isVisitor(next)) picked = String(next.text || "").trim();
    }
    out.push(<div key={m.id} className="mb-4"><Bubble message={m} session={session} picked={picked} /></div>);
  });

  if (session && session.handoff_status === "requested" && !shownHandoff) {
    out.push(<Divider key="handoff_end" tone="warning">Visitor asked for an agent</Divider>);
  }
  if (session && session.rating) {
    out.push(
      <div key="rating" className="mx-auto my-4 flex max-w-sm items-center gap-3 rounded-xl border border-zinc-200 bg-white px-4 py-3">
        <div className="flex">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} className={cn("h-4 w-4", n <= session.rating ? "fill-amber-400 text-amber-400" : "text-zinc-300")} />
          ))}
        </div>
        <div className="min-w-0 text-xs">
          <p className="font-medium text-zinc-900">Visitor rated this chat {session.rating}/5</p>
          {session.rating_comment && <p className="truncate text-zinc-500">"{session.rating_comment}"</p>}
        </div>
      </div>
    );
  }
  if (isResolved) out.push(<Divider key="resolved">Conversation resolved</Divider>);
  return <>{out}</>;
};

export default MessageList;
